import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  MenuItem,
  TextField,
  Stack,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { ADVOCACY_PLATFORM_LABEL } from "../../utils/homeFlow";
import {
  resolveCompetitionMediaType,
  competitionMediaAccept,
  isCompetitionImageFile,
  isCompetitionVideoFile,
} from "../../utils/competitionEventMedia";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import moment from "moment";
import Pagination from "../../component/Pagination";
import {
  contestantAgeValue,
  dobFromAge,
} from "../../utils/contestantProfile";

const statusColor = {
  pending: "warning",
  approved: "success",
  rejected: "error",
};

const DATE_FMT = "DD MMM YYYY, HH:mm";

const ALL = "all";
const PAGE_SIZE = 20;

export default function CompetitionVideos() {
  const token = useSelector((state) => state.auth.token);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState({
    status: ALL,
    competition_id: ALL,
    category: ALL,
    search: "",
  });
  const [actingId, setActingId] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileRow, setProfileRow] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    full_name: "",
    age: "",
    email: "",
    mobile: "",
    bio: "",
    hometown: "",
    profession: "",
    platform_advocacy: "",
    hobbies: "",
  });
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState("");
  const [mediaKind, setMediaKind] = useState(null);
  const [mediaProgress, setMediaProgress] = useState(0);
  const [profileFile, setProfileFile] = useState(null);
  const [profilePreview, setProfilePreview] = useState("");
  const mediaInputRef = useRef(null);
  const profileInputRef = useRef(null);

  const loadCompetitions = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/getAllCompetitions?page=1&limit=100`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const list = res.data?.data?.rows || [];
      setCompetitions(list);
    } catch (e) {
      toast.error(
        e?.response?.data?.message || "Failed to load competitions for filters"
      );
    }
  }, [token]);

  const loadVideos = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(currentPage));
      params.set("limit", String(PAGE_SIZE));
      if (filters.status && filters.status !== ALL)
        params.set("status", filters.status);
      if (filters.competition_id && filters.competition_id !== ALL)
        params.set("competition_id", filters.competition_id);
      if (filters.category && filters.category !== ALL)
        params.set("category", filters.category);
      if (filters.search?.trim())
        params.set("search", filters.search.trim());
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/getCompetitionVideos?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = res.data?.data || {};
      setRows(data.rows || []);
      const count = Number(data.count) || 0;
      setTotalCount(count);
      setTotalPages(Math.max(1, Math.ceil(count / PAGE_SIZE)));
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load submissions");
    } finally {
      setLoading(false);
    }
  }, [token, filters, currentPage]);

  useEffect(() => {
    if (token) loadCompetitions();
  }, [token, loadCompetitions]);

  useEffect(() => {
    if (token) loadVideos();
  }, [token, loadVideos]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const nextSearch = searchInput.trim();
      setFilters((f) => {
        if (f.search === nextSearch) return f;
        setCurrentPage(1);
        return { ...f, search: nextSearch };
      });
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleChangePage = (_, page) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (patch) => {
    setFilters((f) => ({ ...f, ...patch }));
    setCurrentPage(1);
  };

  const handleApprove = async (id) => {
    setActingId(id);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/updateCompetitionVideoStatus`,
        { id, action: "approve" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toast.success(res.data.message || "Video approved successfully");
        loadVideos();
      } else {
        toast.error(res.data.message);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Action failed");
    } finally {
      setActingId(null);
    }
  };

  const openReject = (row) => {
    setRejectTarget(row);
    setRejectReason("");
    setRejectOpen(true);
  };

  const confirmReject = async () => {
    if (!rejectTarget) return;
    const reason = rejectReason.trim();
    if (!reason) {
      toast.error("Please add a rejection comment");
      return;
    }
    setActingId(rejectTarget.id);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/updateCompetitionVideoStatus`,
        {
          id: rejectTarget.id,
          action: "reject",
          rejection_reason: reason,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toast.success(res.data.message || "Video rejected successfully");
        setRejectOpen(false);
        setRejectTarget(null);
        setRejectReason("");
        loadVideos();
      } else {
        toast.error(res.data.message);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Action failed");
    } finally {
      setActingId(null);
    }
  };

  const openEditContestant = (row) => {
    const c = row?.contestant;
    if (!c?.id && !c?.user_id && !c?.user?.id) {
      toast.error("No contestant profile on this row");
      return;
    }
    setEditRow(row);
    setEditForm({
      full_name: c.full_name || "",
      age: contestantAgeValue(c) || "",
      email: c.email || "",
      mobile: c.mobile || "",
      bio: c.bio || "",
      hometown: c.hometown || "",
      profession: c.profession || "",
      platform_advocacy: c.platform_advocacy || "",
      hobbies: c.hobbies || "",
    });
    setMediaFile(null);
    setMediaKind(null);
    setMediaProgress(0);
    setProfileFile(null);
    setProfilePreview(c.profile_image || c.user?.profile_img || "");
    if (profileInputRef.current) profileInputRef.current.value = "";
    const existingThumb =
      row.thumbnail ||
      (resolveCompetitionMediaType(row) === "image" ? row.video_url : "") ||
      "";
    setMediaPreview(existingThumb);
    if (mediaInputRef.current) mediaInputRef.current.value = "";
    setEditOpen(true);
  };

  const saveEditContestant = async () => {
    const c = editRow?.contestant;
    const userId = c?.user_id || c?.user?.id;
    const competitionId =
      c?.competition_id ||
      editRow?.competition_id ||
      (filters.competition_id !== ALL ? filters.competition_id : null);
    if (!userId || !competitionId) {
      toast.error("Missing contestant or competition id");
      return;
    }
    if (!editForm.full_name.trim()) {
      toast.error("Full name is required");
      return;
    }
    const data = new FormData();
    data.append("competition_id", competitionId);
    data.append("user_id", userId);
    data.append("full_name", editForm.full_name.trim());
    if (editForm.age) {
      data.append("age", editForm.age);
      const approxDob = dobFromAge(editForm.age);
      if (approxDob) data.append("dob", approxDob);
    }
    data.append("mobile", editForm.mobile.trim());
    data.append("bio", editForm.bio.trim());
    data.append("hometown", editForm.hometown.trim());
    data.append("profession", editForm.profession.trim());
    data.append("platform_advocacy", editForm.platform_advocacy.trim());
    data.append("hobbies", editForm.hobbies.trim());
    if (profileFile) data.append("profile_image", profileFile);

    setEditSaving(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/adminUpsertContestant`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.data.success) {
        toast.error(res.data.message || "Failed to update contestant");
        return;
      }

      if (mediaFile) {
        const eventId = editRow?.event_id || editRow?.event?.id;
        const category =
          editRow?.category || c?.category || "";
        if (!eventId) {
          toast.error("This row has no event, so media cannot be replaced");
          return;
        }
        const mediaData = new FormData();
        mediaData.append("competition_id", competitionId);
        mediaData.append("user_id", userId);
        mediaData.append("event_id", eventId);
        if (category) mediaData.append("category", category);
        if (c?.id) mediaData.append("contestant_id", c.id);
        mediaData.append("video_id", editRow.id);
        mediaData.append("replace_video_id", editRow.id);
        mediaData.append("replace", "1");
        if (mediaKind === "image") {
          mediaData.append("image", mediaFile);
        } else {
          mediaData.append("video", mediaFile);
        }
        const mediaRes = await axios.post(
          `${process.env.REACT_APP_API_URL}/adminUploadCompetitionEventVideo`,
          mediaData,
          {
            headers: { Authorization: `Bearer ${token}` },
            onUploadProgress: (ev) => {
              if (ev.total) {
                setMediaProgress(Math.round((ev.loaded * 100) / ev.total));
              }
            },
          }
        );
        if (!mediaRes.data.success) {
          toast.error(
            mediaRes.data.message || "Profile saved, but media replace failed"
          );
          return;
        }
      }

      toast.success(
        mediaFile || profileFile
          ? "Contestant details and media updated"
          : res.data.message || "Contestant updated"
      );
      setEditOpen(false);
      setEditRow(null);
      setProfileOpen(false);
      await loadVideos();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to update contestant");
    } finally {
      setEditSaving(false);
    }
  };

  const onPickReplacementMedia = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (isCompetitionImageFile(file)) {
      setMediaKind("image");
    } else if (isCompetitionVideoFile(file)) {
      setMediaKind("video");
    } else {
      toast.error("Choose a video or image file");
      e.target.value = "";
      return;
    }
    setMediaFile(file);
    setMediaPreview(URL.createObjectURL(file));
  };

  const categoryOptions = useMemo(() => {
    const fromComps = new Set();
    const selected = competitions.find(
      (c) => String(c.id) === String(filters.competition_id)
    );
    const source = selected ? [selected] : competitions;
    source.forEach((c) => {
      (c.categories || []).forEach((cat) => {
        const name = typeof cat === "string" ? cat : cat?.category;
        if (name) fromComps.add(name);
      });
    });
    rows.forEach((r) => {
      const videoCat = r.category;
      const contestantCat = r.contestant?.category;
      if (videoCat) fromComps.add(videoCat);
      if (contestantCat) fromComps.add(contestantCat);
    });
    return Array.from(fromComps).sort((a, b) => a.localeCompare(b));
  }, [competitions, filters.competition_id, rows]);

  const filterFieldSx = { minWidth: 180, background: "#fff" };

  return (
    <>
      <ToastContainer />
      <Box className="page_inner" sx={{ px: { xs: 1.5, md: 0 }, pb: 4 }}>
        <Box className="page_title" sx={{ mb: 3 }}>
          <Typography variant="h1" component="h1">
            Competition Media
          </Typography>
          <Typography sx={{ color: "text.secondary", mt: 0.75, fontSize: 14 }}>
            Review contestant video and image uploads — approve to publish.
            Reject with a comment for the contestant.
          </Typography>
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mb: 3, mt: 1, flexWrap: "wrap" }}
        >
          <TextField
            size="small"
            label="Search contestant"
            placeholder="Name or email"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ ...filterFieldSx, minWidth: 260, flex: "1 1 260px" }}
          />
          <TextField
            select
            size="small"
            label="Status"
            value={filters.status}
            onChange={(e) =>
              handleFilterChange({ status: e.target.value })
            }
            sx={filterFieldSx}
          >
            <MenuItem value={ALL}>All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label="Competition"
            value={filters.competition_id}
            onChange={(e) =>
              handleFilterChange({
                competition_id: e.target.value,
                category: ALL,
              })
            }
            sx={{ ...filterFieldSx, minWidth: 240 }}
          >
            <MenuItem value={ALL}>All</MenuItem>
            {competitions.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Category"
            value={filters.category}
            onChange={(e) =>
              handleFilterChange({ category: e.target.value })
            }
            sx={filterFieldSx}
          >
            <MenuItem value={ALL}>All</MenuItem>
            {categoryOptions.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 2,
            border: "1px solid #eceef3",
            boxShadow: "none",
          }}
        >
          <Table sx={{ minWidth: 1100 }}>
            <TableHead>
              <TableRow>
                <TableCell>Media</TableCell>
                <TableCell>Competition</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Contestant</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Uploaded</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress
                      size={28}
                      sx={{ color: "#ef364e", my: 2 }}
                    />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No submissions found
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const user = row.contestant?.user;
                  const displayName =
                    row.contestant?.full_name ||
                    (user
                      ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
                      : "");
                  const categoryLabel =
                    row.category || row.contestant?.category || "—";
                  const mediaType = resolveCompetitionMediaType(row);
                  const thumbSrc =
                    row.thumbnail ||
                    (mediaType === "image" ? row.video_url : null);
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        {thumbSrc || row.video_url ? (
                          <Box
                            component="a"
                            href={row.video_url}
                            target="_blank"
                            rel="noreferrer"
                            sx={{
                              display: "block",
                              position: "relative",
                              width: 88,
                              height: 58,
                              borderRadius: 1,
                              overflow: "hidden",
                              backgroundImage: `url(${thumbSrc || row.video_url})`,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                            }}
                          >
                            <Box
                              sx={{
                                position: "absolute",
                                left: 6,
                                bottom: 5,
                                color: "#fff",
                                fontFamily:
                                  '"Montserrat", sans-serif',
                                fontStyle: "italic",
                                fontSize: 11,
                                letterSpacing: "0.04em",
                                textTransform: "uppercase",
                                textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                                pointerEvents: "none",
                              }}
                            >
                              {mediaType === "image" ? "Image" : "Video"}
                            </Box>
                          </Box>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>
                        {row.contestant?.competition?.name || "—"}
                      </TableCell>
                      <TableCell>{row.event?.name || "—"}</TableCell>
                      <TableCell>{categoryLabel}</TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Box
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: "50%",
                              flexShrink: 0,
                              bgcolor: "#e5e7eb",
                              backgroundImage: row.contestant?.profile_image
                                ? `url(${row.contestant.profile_image})`
                                : "none",
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                              border: "1px solid #e5e7eb",
                            }}
                          />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: 14,
                                color: "#111827",
                              }}
                            >
                              {displayName || user?.email || "—"}
                            </Typography>
                            <Typography
                              sx={{ color: "#4b5563", fontSize: 12, mt: 0.25 }}
                              noWrap
                            >
                              {row.contestant?.email || user?.email || "—"}
                            </Typography>
                            {row.rejection_reason ? (
                              <Typography
                                sx={{ color: "#b91c1c", fontSize: 12, mt: 0.5 }}
                                noWrap
                              >
                                Reject: {row.rejection_reason}
                              </Typography>
                            ) : null}
                            <Stack direction="row" spacing={1.5} sx={{ mt: 0.5 }}>
                              <Button
                                size="small"
                                onClick={() => {
                                  setProfileRow(row);
                                  setProfileOpen(true);
                                }}
                                sx={{
                                  px: 0,
                                  minWidth: 0,
                                  textTransform: "none",
                                  fontWeight: 700,
                                  color: "#ef364e",
                                }}
                              >
                                View full profile
                              </Button>
                              <Button
                                size="small"
                                onClick={() => openEditContestant(row)}
                                sx={{
                                  px: 0,
                                  minWidth: 0,
                                  textTransform: "none",
                                  fontWeight: 700,
                                  color: "#ef364e",
                                }}
                              >
                                Edit details
                              </Button>
                            </Stack>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.status}
                          color={statusColor[row.status] || "default"}
                          sx={{ textTransform: "capitalize", fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {moment(row.createdAt).format(DATE_FMT)}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" justifyContent="flex-end">
                          <Tooltip title="Edit contestant details">
                            <span>
                              <IconButton
                                disabled={!row.contestant}
                                onClick={() => openEditContestant(row)}
                                sx={{ color: "#ef364e" }}
                              >
                                <EditOutlinedIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {row.status === "pending" ? (
                            <>
                              <Tooltip title="Approve (also approves profile)">
                                <span>
                                  <IconButton
                                    color="success"
                                    disabled={actingId === row.id}
                                    onClick={() => handleApprove(row.id)}
                                  >
                                    <CheckCircleOutlineIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Reject with comment">
                                <span>
                                  <IconButton
                                    color="error"
                                    disabled={actingId === row.id}
                                    onClick={() => openReject(row)}
                                  >
                                    <HighlightOffIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </>
                          ) : row.status === "approved" ? (
                            <Tooltip title="Reject with comment">
                              <span>
                                <IconButton
                                  color="error"
                                  disabled={actingId === row.id}
                                  onClick={() => openReject(row)}
                                >
                                  <HighlightOffIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Approve (also approves profile)">
                              <span>
                                <IconButton
                                  color="success"
                                  disabled={actingId === row.id}
                                  onClick={() => handleApprove(row.id)}
                                >
                                  <CheckCircleOutlineIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!loading && totalCount > 0 && (
          <Typography sx={{ mt: 2, color: "text.secondary", fontSize: 13 }}>
            Showing {(currentPage - 1) * PAGE_SIZE + 1}–
            {Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount}{" "}
            submissions
          </Typography>
        )}

        {totalPages > 1 && (
          <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              handleChangePage={handleChangePage}
            />
          </Box>
        )}
      </Box>

      <Dialog
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 24px 64px rgba(15,23,42,0.18)",
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            pt: 2.5,
            pb: 2,
            background:
              "linear-gradient(135deg, #1f2340 0%, #2d3358 55%, #3a2a3a 100%)",
          }}
        >
          <Typography
            sx={{
              color: "rgba(255,255,255,0.65)",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              mb: 2,
            }}
          >
            Contestant registration
          </Typography>
          {profileRow?.contestant ? (
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 76,
                  height: 76,
                  borderRadius: "50%",
                  flexShrink: 0,
                  bgcolor: "rgba(255,255,255,0.12)",
                  backgroundImage: profileRow.contestant.profile_image
                    ? `url(${profileRow.contestant.profile_image})`
                    : "none",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  border: "3px solid rgba(255,205,31,0.65)",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    color: "#fff",
                    fontWeight: 800,
                    fontSize: 20,
                    lineHeight: 1.25,
                    letterSpacing: "-0.02em",
                  }}
                >
                  {profileRow.contestant.full_name ||
                    `${profileRow.contestant.user?.firstName || ""} ${
                      profileRow.contestant.user?.lastName || ""
                    }`.trim() ||
                    "—"}
                </Typography>
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    fontSize: 13,
                    mt: 0.5,
                  }}
                  noWrap
                >
                  {profileRow.contestant.competition?.name || "Competition"}
                </Typography>
              </Box>
            </Stack>
          ) : null}
        </Box>

        <DialogContent sx={{ px: 3, py: 2.5 }}>
          {profileRow?.contestant ? (
            <Stack spacing={1.25}>
              {[
                ["Full name", profileRow.contestant.full_name],
                ["Age", contestantAgeValue(profileRow.contestant) || null],
                ["Email address", profileRow.contestant.email],
                ["Mobile number", profileRow.contestant.mobile],
                ["Bio", profileRow.contestant.bio],
                ["Hometown", profileRow.contestant.hometown],
                ["Profession", profileRow.contestant.profession],
                [
                  ADVOCACY_PLATFORM_LABEL,
                  profileRow.contestant.platform_advocacy,
                ],
                ["Hobbies", profileRow.contestant.hobbies],
                ["Competition", profileRow.contestant.competition?.name],
              ].map(([label, value]) => (
                <Box
                  key={label}
                  sx={{
                    px: 1.75,
                    py: 1.35,
                    borderRadius: 2,
                    bgcolor: "#f8fafc",
                    border: "1px solid #eef2f7",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#64748b",
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      mb: 0.35,
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 15,
                      color: "#0f172a",
                      wordBreak: "break-word",
                    }}
                  >
                    {value || "—"}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography>No contestant data</Typography>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2.5,
            pt: 0,
            justifyContent: "space-between",
          }}
        >
          {profileRow?.contestant?.profile_image ? (
            <Button
              component="a"
              href={profileRow.contestant.profile_image}
              target="_blank"
              rel="noreferrer"
              sx={{
                textTransform: "none",
                fontWeight: 700,
                color: "#ef364e",
              }}
            >
              Open photo
            </Button>
          ) : (
            <span />
          )}
          <Button
            variant="outlined"
            onClick={() => {
              setProfileOpen(false);
              openEditContestant(profileRow);
            }}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              px: 2.5,
              color: "#ef364e",
              borderColor: "#ef364e",
              "&:hover": {
                borderColor: "#d32d43",
                bgcolor: "rgba(239,54,78,0.06)",
              },
            }}
          >
            Edit details
          </Button>
          <Button
            variant="contained"
            onClick={() => setProfileOpen(false)}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              bgcolor: "#1f2340",
              borderRadius: 2,
              px: 2.5,
              "&:hover": { bgcolor: "#2d3358" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editOpen}
        onClose={() => !editSaving && setEditOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit contestant details</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "text.secondary", mb: 2, fontSize: 14 }}>
            Update contestant profile or replace event media at any time. Email
            cannot be changed. Approved entries stay approved when replaced by
            admin.
          </Typography>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {profilePreview ? (
              <Box
                component="img"
                src={profilePreview}
                alt="Profile"
                sx={{
                  width: 96,
                  height: 96,
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "1px solid #e2e8f0",
                }}
              />
            ) : null}
            <Button
              variant="outlined"
              component="label"
              size="small"
              sx={{ alignSelf: "flex-start", textTransform: "none" }}
            >
              {profileFile ? profileFile.name : "Change profile photo"}
              <input
                ref={profileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (!file) return;
                  if (!file.type?.startsWith("image/")) {
                    toast.error("Please select a valid image file");
                    return;
                  }
                  setProfileFile(file);
                  setProfilePreview(URL.createObjectURL(file));
                }}
              />
            </Button>
            {[
              ["full_name", "Full name"],
              ["age", "Age"],
              ["email", "Email"],
              ["mobile", "Mobile"],
              ["hometown", "Hometown"],
              ["profession", "Profession"],
            ].map(([key, label]) => (
              <TextField
                key={key}
                label={label}
                size="small"
                type={key === "age" ? "number" : "text"}
                value={editForm[key]}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, [key]: e.target.value }))
                }
                disabled={key === "email"}
                helperText={key === "email" ? "Cannot be changed" : undefined}
                fullWidth
              />
            ))}
            <TextField
              label="Bio"
              size="small"
              value={editForm.bio}
              onChange={(e) =>
                setEditForm((prev) => ({ ...prev, bio: e.target.value }))
              }
              multiline
              minRows={2}
              fullWidth
            />
            <TextField
              label={ADVOCACY_PLATFORM_LABEL}
              size="small"
              value={editForm.platform_advocacy}
              onChange={(e) =>
                setEditForm((prev) => ({
                  ...prev,
                  platform_advocacy: e.target.value,
                }))
              }
              multiline
              minRows={2}
              fullWidth
            />
            <TextField
              label="Hobbies"
              size="small"
              value={editForm.hobbies}
              onChange={(e) =>
                setEditForm((prev) => ({ ...prev, hobbies: e.target.value }))
              }
              multiline
              minRows={2}
              fullWidth
            />
            <Box
              sx={{
                pt: 1,
                borderTop: "1px solid #edf0f2",
              }}
            >
              <Typography sx={{ fontWeight: 700, mb: 0.5, fontSize: 14 }}>
                Replace video or image
              </Typography>
              <Typography sx={{ color: "text.secondary", fontSize: 13, mb: 1.5 }}>
                Re-upload this contestant’s media for{" "}
                {editRow?.event?.name || "this event"}. The new file replaces the
                current submission.
              </Typography>
              {mediaPreview ? (
                mediaKind === "video" ||
                (!mediaFile &&
                  resolveCompetitionMediaType(editRow) === "video") ? (
                  <Box
                    component="video"
                    src={mediaFile ? mediaPreview : editRow?.video_url}
                    controls
                    muted
                    sx={{
                      width: "100%",
                      maxHeight: 180,
                      display: "block",
                      borderRadius: 1,
                      mb: 1.5,
                      bgcolor: "#0f1219",
                    }}
                  />
                ) : (
                  <Box
                    component="img"
                    src={mediaPreview}
                    alt=""
                    sx={{
                      width: "100%",
                      maxHeight: 180,
                      objectFit: "contain",
                      borderRadius: 1,
                      mb: 1.5,
                      bgcolor: "#0f1219",
                    }}
                  />
                )
              ) : null}
              <input
                ref={mediaInputRef}
                type="file"
                hidden
                accept={competitionMediaAccept}
                onChange={onPickReplacementMedia}
              />
              <Button
                variant="outlined"
                onClick={() => mediaInputRef.current?.click()}
                disabled={editSaving}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  color: "#ef364e",
                  borderColor: "#ef364e",
                  "&:hover": {
                    borderColor: "#d32d43",
                    bgcolor: "rgba(239,54,78,0.06)",
                  },
                }}
              >
                {mediaFile ? "Choose a different file" : "Choose file to re-upload"}
              </Button>
              {mediaFile ? (
                <Typography sx={{ mt: 1, fontSize: 13, color: "#334155" }}>
                  {mediaFile.name}
                  {mediaKind ? ` · ${mediaKind}` : ""}
                </Typography>
              ) : null}
              {editSaving && mediaFile && mediaProgress > 0 ? (
                <LinearProgress
                  variant="determinate"
                  value={mediaProgress}
                  sx={{
                    mt: 1.5,
                    borderRadius: 1,
                    bgcolor: "rgba(239,54,78,0.12)",
                    "& .MuiLinearProgress-bar": { bgcolor: "#ef364e" },
                  }}
                />
              ) : null}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setEditOpen(false)}
            disabled={editSaving}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: "#64748b",
              "&:hover": { bgcolor: "rgba(15,23,42,0.04)" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={editSaving}
            onClick={saveEditContestant}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              bgcolor: "#ef364e",
              boxShadow: "none",
              "&:hover": { bgcolor: "#d32d43", boxShadow: "none" },
              "&.Mui-disabled": {
                bgcolor: "rgba(239,54,78,0.4)",
                color: "#fff",
              },
            }}
          >
            {editSaving ? "Saving…" : "Save changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rejectOpen}
        onClose={() => !actingId && setRejectOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reject submission</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "text.secondary", mb: 2, fontSize: 14 }}>
            Add a comment so the contestant knows what to fix. They can
            re-upload a video or image after rejection.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Rejection comment"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setRejectOpen(false)}
            disabled={Boolean(actingId)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={Boolean(actingId) || !rejectReason.trim()}
            onClick={confirmReject}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {actingId ? "Rejecting…" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
